import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type VoiceNavigationSource =
  | 'patient-details'
  | 'appointment-details'
  | 'schedule'
  | 'dashboard'
  | 'records'
  | 'other';

export interface VoiceNavigationContext {
  // Navigation context
  returnPath: string;
  source: VoiceNavigationSource;

  // Patient context
  patientId?: string;
  patientName?: string;

  // Appointment context
  appointmentId?: string;
  appointmentDate?: string;
  appointmentReason?: string;

  // Recording state
  isRecording: boolean;
  recordingStartedAt?: Date;

  // Voice transcript (for review before creating record)
  voiceTranscript?: string;
  soapData?: unknown;
}

interface VoiceNavigationStore {
  // Current context
  context: VoiceNavigationContext | null;

  // Actions
  startVoiceRecording: (context: Omit<VoiceNavigationContext, 'isRecording'>) => void;
  stopVoiceRecording: () => void;
  updateTranscript: (transcript: string, soapData?: unknown) => void;
  clearContext: () => void;
  setReturnPath: (returnPath: string) => void;
  getAddRecordUrl: () => string;
}

export const useVoiceNavigationStore = create<VoiceNavigationStore>()(
  persist(
    (set, get) => ({
      context: null,

      startVoiceRecording: (contextData) => {
        console.log('🎤 Starting voice recording with context:', contextData);
        set({
          context: {
            ...contextData,
            isRecording: true,
            recordingStartedAt: new Date(),
          },
        });
      },

      stopVoiceRecording: () => {
        console.log('🛑 Stopping voice recording');
        set((state) => ({
          context: state.context
            ? { ...state.context, isRecording: false }
            : null,
        }));
      },

      updateTranscript: (transcript, soapData) => {
        console.log('📝 Updating voice transcript');
        set((state) => ({
          context: state.context
            ? { ...state.context, voiceTranscript: transcript, soapData }
            : null,
        }));
      },

      clearContext: () => {
        console.log('🧹 Clearing voice navigation context');
        set({ context: null });
      },

      setReturnPath: (returnPath) => {
        set((state) => ({
          context: state.context
            ? { ...state.context, returnPath }
            : { returnPath, source: 'other', isRecording: false },
        }));
      },

      getAddRecordUrl: () => {
        const { context } = get();
        // The encounter flow lives on the patient page now; without a patient
        // we can only send the user to the picker.
        if (!context?.patientId) return '/patients';

        const params = new URLSearchParams({
          record: 'new',
          autoStart: 'true',
        });
        if (context.appointmentId) params.append('appointmentId', context.appointmentId);

        const url = `/patients/${context.patientId}?${params.toString()}`;
        console.log('🔗 Generated encounter URL:', url);
        return url;
      },
    }),
    {
      name: 'voice-navigation-storage',
    }
  )
);
